import Ellipsis from "@/Components/Base/Ellipsis";
import Icon from "@/Components/Icon/Icon";
import useLayoutData from "@/shared/hooks/useLayoutData";
import useRoleHooks from "@/shared/hooks/useRoleHooks";
import { Branch, BranchGroup } from "@/shared/utils/interfaces";
import { Children, useState } from "react";
import { components as rsComponents } from "react-select";
import { useTranslation } from "react-i18next";
import { List } from "react-window";
import Checkbox from "../Checkbox/Checkbox";
import Select from "./Select";
import Tooltip from "@/Components/Tooltip/Tooltip";
import { codeToCountryIcon } from "@/shared/utils/countries";
import { CountryCode } from "@/shared/utils/enum";

export const BranchStatus = ({ isActive, className = "", ...props }) => (
  <span
    className={`absolute right-0 top-2 inline-block h-4 w-4 rounded ${className}`.trim()}
    style={{ backgroundColor: isActive ? "var(--success)" : "var(--error)" }}
    {...props}
  />
);

export const BranchSelectItem = ({ option }) => {
  const { t } = useTranslation();

  return (
    <div className="flex items-center gap-6 text-black">
      <Icon
        className="size-14 rounded-md"
        variants={
          codeToCountryIcon[
            option.franchise?.country || CountryCode.NETHERLANDS
          ]
        }
      />
      <div className="w-[80%]">
        <div className="leading-[1.1] items-center">
          <div className="font-semibold text-[1.3rem] relative">
            <Ellipsis>{option.franchise.name}</Ellipsis>
            <BranchStatus isActive={option.franchise.is_active} />
          </div>
          <div
            className="text-[1.7rem] relative"
            style={{ fontWeight: "bold" }}
          >
            <Ellipsis>{option.branch.name}</Ellipsis>
            <BranchStatus isActive={option.branch.is_active === "1"} />
          </div>
        </div>
        <div className="flex gap-2 leading-[1.1]">
          <div>
            <span className="font-medium">{t("franchiseId")}:</span>
            <span> {option.franchise.id}</span>
          </div>
          <div>
            <span className="font-medium">{t("branchId")}:</span>
            <span> {option.branch.id}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

const ITEM_HEIGHT = 60;
const MAX_VISIBLE_ITEMS = 5;

const VirtualizedMenuList = (props) => {
  const { children, maxHeight } = props;
  const childrenArray = Children.toArray(children);

  if (!childrenArray.length) {
    return <rsComponents.MenuList {...props}>{children}</rsComponents.MenuList>;
  }

  const listHeight = Math.min(
    maxHeight,
    childrenArray.length * ITEM_HEIGHT,
    MAX_VISIBLE_ITEMS * ITEM_HEIGHT
  );

  return (
    <List<{ items: React.ReactNode[] }>
      style={{ height: listHeight }}
      rowCount={childrenArray.length}
      rowHeight={ITEM_HEIGHT}
      rowComponent={({ index, style, items }) => (
        <div style={style}>{items[index]}</div>
      )}
      rowProps={{ items: childrenArray }}
    />
  );
};

const BranchSelect = ({
  onChange,
  customBranches,
  value = null,
  showAllBox = true,
}: {
  customBranches?: BranchGroup[];
  onChange?: (data: BranchGroup) => void;
  value?: Branch;
  showAllBox?: boolean;
}) => {
  const { t } = useTranslation();
  const [showAll, setShowAll] = useState(false);
  const { isAppsmenEmployee, hasAdminAuthority } = useRoleHooks();

  const {
    branch,
    setBranch,
    findBranch,
    setFranchise,
    currentBranches,
    setMerchant,
  } = useLayoutData();

  return (
    <div className="inline-block relative w-full">
      <Select
        styles={{
          menuPortal: (base) => ({
            ...base,
            zIndex: 9999,
          }),
        }}
        components={{ MenuList: VirtualizedMenuList }}
        menuPortalTarget={document.getElementById("root")}
        placeholder={t("selectBranch")}
        className="branch-select-container"
        classNamePrefix="branch-select"
        value={onChange ? findBranch(value?.id) : findBranch(branch?.id)}
        onChange={
          onChange
            ? (v) => onChange(v)
            : (v) => {
                setFranchise(v.franchise.id);
                setBranch(v.branch);
                setMerchant(v.merchant.id);
              }
        }
        isSearchable
        getOptionLabel={(option) => <BranchSelectItem option={option} />}
        filterOption={(option, searchText) => {
          const label =
            `${option.data.branch.id}${option.data.branch.name}${option.data.franchise.id}${option.data.franchise.name}`?.toLowerCase();
          return label.includes(searchText.toLowerCase()) ? true : false;
        }}
        getOptionValue={(option) => option.branch.id}
        options={(customBranches ?? currentBranches).filter((b) =>
          showAll && b.branch.id !== branch.id
            ? true
            : b.branch.is_active === "1" && b.franchise.is_active
        )}
      />

      {showAllBox && (hasAdminAuthority || isAppsmenEmployee) && (
        <Tooltip content={t("showAll")}>
          <div>
            <div className="absolute top-7 -right-12 hidden md:block">
              <Checkbox
                active={showAll}
                onClick={() => setShowAll(!showAll)}
                sizing="small"
              />
            </div>
          </div>
        </Tooltip>
      )}
    </div>
  );
};

export default BranchSelect;
